import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, Shield, Users, BookOpen, Brain, AlertTriangle,
  CheckCircle, Info, Lightbulb, Target, Calendar, Clock,
  User, MessageCircle, Phone, Search, Star, Award,
  Activity, TrendingUp, Zap, Eye, EyeOff, HelpCircle,
  Stethoscope, UserCheck, Lock, Globe, Clipboard,
  Trophy, Gift, Gamepad2, ArrowRight, Plus, Minus,
  Sparkles, Coffee, Wind
} from 'lucide-react';
import { sexualHealthData } from './SexualHealthData';


  export const IndependentAnatomyCard = ({ data, title, subtitle, gradientFrom, gradientTo, accentColor, emoji }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    
    const handleNext = () => {
      if (isAnimating) return;
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev + 1) % data.length);
      setTimeout(() => setIsAnimating(false), 300);
    };

    const handlePrev = () => {
      if (isAnimating) return;
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev - 1 + data.length) % data.length);
      setTimeout(() => setIsAnimating(false), 300);
    };

    const currentData = data[currentIndex];

    return (
      <div className={`relative bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-2xl p-1 shadow-2xl hover:shadow-3xl transition-all duration-300`}>
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-2xl blur-sm"></div>
        
        <div className="relative bg-white rounded-2xl p-6 h-[600px] overflow-hidden">
          <div className="absolute top-4 right-4 text-4xl opacity-10">{emoji}</div>
          <div className="absolute bottom-4 left-4 text-6xl opacity-5">{emoji}</div>
          
          <div className="mb-6">
            <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white px-4 py-2 rounded-full mb-3`}>
              <span className="text-lg">{emoji}</span>
              <span className="font-semibold text-sm">{title}</span>
            </div>
            <p className="text-gray-600 text-sm">{subtitle}</p>
          </div>

          <div className="flex justify-between items-center mb-6">
            <div className="flex space-x-1">
              {data.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    idx === currentIndex 
                      ? `bg-gradient-to-r ${gradientFrom} ${gradientTo}` 
                      : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handlePrev}
                className={`w-8 h-8 rounded-full bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white flex items-center justify-center hover:scale-110 transition-transform shadow-lg`}
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
              </button>
              <button
                onClick={handleNext}
                className={`w-8 h-8 rounded-full bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white flex items-center justify-center hover:scale-110 transition-transform shadow-lg`}
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <div className={`bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white p-4 rounded-xl relative overflow-hidden`}>
              <div className="absolute top-0 right-0 text-6xl opacity-20">{emoji}</div>
              <h3 className="text-xl font-bold mb-1 relative z-10">{currentData.name}</h3>
              <div className="text-xs bg-white/20 inline-block px-2 py-1 rounded-full">
                Part {currentIndex + 1} of {data.length}
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 border-l-4 border-gradient-to-b from-blue-400 to-purple-400">
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <Brain className="w-4 h-4 text-blue-600" />
                Function & Purpose
              </h4>
              <p className="text-gray-700 text-sm leading-relaxed">{currentData.description}</p>
            </div>

            <div className="bg-green-50 rounded-xl p-4 border-l-4 border-green-400">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Heart className="w-4 h-4 text-green-600" />
                Health Tips & Care
              </h4>
              <div className="space-y-2">
                {currentData.techniques.slice(0, 3).map((tip, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <div className={`w-5 h-5 rounded-full bg-gradient-to-r ${gradientFrom} ${gradientTo} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm text-gray-700">{tip}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center pt-4">
              <div className="inline-flex items-center gap-2 text-xs text-gray-500 bg-gray-100 px-3 py-2 rounded-full">
                <ArrowRight className="w-3 h-3" />
                <span>Swipe or click arrows to explore more</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  };

  export const AnatomySection = () => {
    const [activeSection, setActiveSection] = useState('overview');
    
    const renderOverview = () => (
      <div className="space-y-6 sm:space-y-8">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Body Knowledge Hub</h2>
              <p className="text-xs sm:text-sm text-gray-600">Learn about anatomy and health</p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {[
            { 
              id: "male",
              title: "Male Anatomy", 
              icon: User, 
              color: "blue", 
              description: "Understanding male body parts",
              count: Object.keys(sexualHealthData.sexualAnatomyDetailed.maleAnatomyAdvanced).length,
            },
            { 
              id: "female",
              title: "Female Anatomy", 
              icon: Heart, 
              color: "pink", 
              description: "Understanding female body parts",
              count: Object.keys(sexualHealthData.sexualAnatomyDetailed.femaleAnatomyAdvanced).length,
            },
            { 
              id: "health",
              title: "Health Tips", 
              icon: Shield, 
              color: "green", 
              description: "Care and wellness advice",
              count: "Essential",
            }
          ].map((item, index) => (
            <motion.button
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setActiveSection(item.id)}
              className={`bg-white rounded-xl shadow-lg p-4 sm:p-6 text-center hover:shadow-xl transition-all border-l-4 group cursor-pointer ${
                item.color === 'blue' ? 'border-blue-500 hover:border-blue-600' :
                item.color === 'pink' ? 'border-pink-500 hover:border-pink-600' :
                item.color === 'green' ? 'border-green-500 hover:border-green-600' : 'border-gray-500'
              }`}
            >
              <div className="text-4xl mb-3">{item.emoji}</div>
              <item.icon className={`w-8 h-8 sm:w-10 sm:h-10 mb-3 sm:mb-4 mx-auto group-hover:scale-110 transition-transform ${
                item.color === 'blue' ? 'text-blue-600' :
                item.color === 'pink' ? 'text-pink-600' :
                item.color === 'green' ? 'text-green-600' : 'text-gray-600'
              }`} />
              <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-sm text-gray-600 mb-2">{item.description}</p>
              <div className={`text-xs font-medium px-3 py-1 rounded-full inline-block ${
                item.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                item.color === 'pink' ? 'bg-pink-100 text-pink-700' :
                item.color === 'green' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {item.count} topics
              </div>
              <div className="mt-3 text-xs text-gray-400 flex items-center justify-center gap-1">
                <span>Click to explore</span>
                <ArrowRight className="w-3 h-3" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    );

    return (
      <div className="space-y-6 sm:space-y-8">
        {activeSection === 'overview' && renderOverview()}
        
        {activeSection === 'male' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveSection('overview')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <IndependentAnatomyCard 
              data={Object.entries(sexualHealthData.sexualAnatomyDetailed.maleAnatomyAdvanced).map(([name, info]) => ({
                name: name.charAt(0).toUpperCase() + name.slice(1),
                description: info.function,
                techniques: info.healthTips
              }))}
              title="👨 Male Anatomy Guide"
              subtitle="Essential knowledge for men's health and wellness"
              gradientFrom="from-blue-500"
              gradientTo="to-cyan-500"
              accentColor="blue"
              emoji="🧬"
            />
          </div>
        )}
        
        {activeSection === 'female' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveSection('overview')}
                className="flex items-center gap-2 text-pink-600 hover:text-pink-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <IndependentAnatomyCard 
              data={Object.entries(sexualHealthData.sexualAnatomyDetailed.femaleAnatomyAdvanced).map(([name, info]) => ({
                name: name === 'gSpot' ? 'G-Spot' : name.charAt(0).toUpperCase() + name.slice(1),
                description: info.function,
                techniques: info.healthTips
              }))}
              title="👩 Female Anatomy Guide"
              subtitle="Essential knowledge for women's health and wellness"
              gradientFrom="from-pink-500"
              gradientTo="to-purple-500"
              accentColor="pink"
              emoji="🌸"
            />
          </div>
        )}
        
        {activeSection === 'health' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveSection('overview')}
                className="flex items-center gap-2 text-green-600 hover:text-green-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  title: "Daily Hygiene",
                  emoji: "🧼",
                  tips: [
                    "Wash with warm water and mild soap daily",
                    "Avoid harsh chemicals or douching",
                    "Wear breathable cotton underwear",
                    "Change underwear daily or when sweaty"
                  ]
                },
                {
                  title: "Regular Check-ups",
                  emoji: "🩺",
                  tips: [
                    "Annual physical exams are important",
                    "Self-examinations for unusual changes",
                    "STI testing as recommended by doctor",
                    "Don't ignore persistent symptoms"
                  ]
                },
                {
                  title: "Healthy Lifestyle",
                  emoji: "💪",
                  tips: [
                    "Regular exercise improves circulation",
                    "Balanced diet supports hormone health",
                    "Stay hydrated for optimal body function",
                    "Manage stress for overall wellness"
                  ]
                },
                {
                  title: "Safe Practices",
                  emoji: "🛡️",
                  tips: [
                    "Use protection during sexual activity",
                    "Communicate openly with partners",
                    "Know your body and what's normal for you",
                    "Seek help when something feels wrong"
                  ]
                },
                {
                  title: "Mental Health",
                  emoji: "🧠",
                  tips: [
                    "Body positivity is important for health",
                    "Address concerns without shame",
                    "Seek professional help when needed",
                    "Remember that everyone's body is different"
                  ]
                },
                {
                  title: "Warning Signs",
                  emoji: "⚠️",
                  tips: [
                    "Unusual discharge, odor, or itching",
                    "Persistent pain during intercourse",
                    "Unexplained bumps, sores, or rashes",
                    "Changes in urination patterns"
                  ]
                }
              ].map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500"
                >
                  <div className="text-center mb-4">
                    <div className="text-4xl mb-2">{section.emoji}</div>
                    <h3 className="text-lg font-bold text-gray-800">{section.title}</h3>
                  </div>
                  
                  <div className="space-y-2">
                    {section.tips.map((tip, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{tip}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

      
      </div>
    );
  };

  export const EnhancedContraceptionCard = ({ data, title, subtitle, gradientFrom, gradientTo, emoji }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    
    const handleNext = () => {
      if (isAnimating) return;
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev + 1) % data.length);
      setTimeout(() => setIsAnimating(false), 300);
    };

    const handlePrev = () => {
      if (isAnimating) return;
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev - 1 + data.length) % data.length);
      setTimeout(() => setIsAnimating(false), 300);
    };

    const currentData = data[currentIndex];

    return (
      <div className={`relative bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-3xl p-1 shadow-2xl hover:shadow-3xl transition-all duration-300`}>
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-3xl blur-sm"></div>
        
        <div className="relative bg-white rounded-3xl p-8 overflow-hidden">
          <div className="absolute top-6 right-6 text-6xl opacity-5">{emoji}</div>
          <div className="absolute bottom-6 left-6 text-8xl opacity-5">{emoji}</div>
          
          <div className="text-center mb-8">
            <div className={`inline-flex items-center gap-3 bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white px-6 py-3 rounded-full mb-4`}>
              <span className="text-2xl">{emoji}</span>
              <span className="font-bold text-lg">{title}</span>
            </div>
            <p className="text-gray-600 text-lg">{subtitle}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="space-y-6">
              <div className={`bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white p-6 rounded-2xl relative overflow-hidden`}>
                <div className="absolute top-0 right-0 text-8xl opacity-10">{emoji}</div>
                <h3 className="text-2xl font-bold mb-2 relative z-10">{currentData.name}</h3>
                <div className="flex gap-2 mb-3">
                  <span className="text-xs bg-white/20 px-3 py-1 rounded-full">{currentData.type}</span>
                  <span className="text-xs bg-white/20 px-3 py-1 rounded-full">{currentData.effectiveness}</span>
                </div>
                <p className="text-white/90 text-sm leading-relaxed">
                  Learn about effectiveness, benefits, and considerations
                </p>
              </div>

              <div className="bg-green-50 rounded-xl p-6 border-l-4 border-green-400">
                <h5 className="text-lg font-bold text-green-800 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                   Benefits & Advantages
                </h5>
                <div className="space-y-2">
                  {currentData.pros.map((pro, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{pro}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-orange-50 rounded-xl p-6 border-l-4 border-orange-400">
                <h5 className="text-lg font-bold text-orange-800 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                   Considerations & Drawbacks
                </h5>
                <div className="space-y-2">
                  {currentData.cons.map((con, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <AlertTriangle className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{con}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-400">
                <h5 className="text-lg font-bold text-blue-800 mb-3 flex items-center gap-2">
                  <Target className="w-5 h-5" />
                   Best For
                </h5>
                <div className="bg-blue-100 rounded-lg p-4">
                  <p className="text-blue-800 font-semibold text-base">{currentData.suitability}</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200">
                <div className="text-center">
                  <div className="text-8xl mb-4">{emoji}</div>
                  <h4 className="text-2xl font-bold text-gray-700 mb-4">{currentData.name}</h4>
                  
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <h5 className="font-bold text-gray-800 mb-2">🎯 Effectiveness Rate</h5>
                      <div className="text-2xl font-bold text-green-600">{currentData.effectiveness}</div>
                      <p className="text-sm text-gray-600">When used correctly</p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <h5 className="font-bold text-gray-800 mb-2">🏷️ Method Type</h5>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        currentData.type === 'Barrier' ? 'bg-blue-100 text-blue-800' :
                        currentData.type === 'Hormonal' ? 'bg-purple-100 text-purple-800' :
                        currentData.type === 'Long-acting' ? 'bg-indigo-100 text-indigo-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {currentData.type}
                      </span>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <h5 className="font-bold text-gray-800 mb-2">🏥 STI Protection</h5>
                      <p className="text-gray-600">
                        {currentData.name.toLowerCase().includes('condom') ? 
                          '✅ Provides STI protection' : 
                          '❌ No STI protection'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <div className="flex space-x-1">
              {data.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    idx === currentIndex 
                      ? `bg-gradient-to-r ${gradientFrom} ${gradientTo}` 
                      : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-2">Method {currentIndex + 1} of {data.length}</div>
              <div className="flex gap-3">
                <button
                  onClick={handlePrev}
                  className={`w-12 h-12 rounded-full bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white flex items-center justify-center hover:scale-110 transition-transform shadow-lg`}
                >
                  <ArrowRight className="w-5 h-5 rotate-180" />
                </button>
                <button
                  onClick={handleNext}
                  className={`w-12 h-12 rounded-full bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white flex items-center justify-center hover:scale-110 transition-transform shadow-lg`}
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="text-right text-xs text-gray-500">
              Effectiveness: {currentData.effectiveness}<br />
              Type: {currentData.type}
            </div>
          </div>
        </div>
      </div>
    );
  };

export const ContraceptionSection = () => {
    const [activeSection, setActiveSection] = useState('overview');
    
    const renderOverview = () => (
      <div className="space-y-6 sm:space-y-8">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Protection Methods Hub</h2>
              <p className="text-xs sm:text-sm text-gray-600">Choose what works for you</p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {[
            { 
              id: "methods",
              title: "Contraception Methods", 
              icon: Shield, 
              color: "green", 
              description: "Explore different protection options",
              count: sexualHealthData.contraception.methods.length,
            },
            { 
              id: "emergency",
              title: "Emergency Options", 
              icon: Clock, 
              color: "orange", 
              description: "When you need immediate help",
              count: sexualHealthData.contraception.emergencyContraception.length,
            },
            { 
              id: "choices",
              title: "Smart Choices", 
              icon: Lightbulb, 
              color: "blue", 
              description: "Tips for choosing right method",
              count: "Essential",
            }
          ].map((item, index) => (
            <motion.button
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setActiveSection(item.id)}
              className={`bg-white rounded-xl shadow-lg p-4 sm:p-6 text-center hover:shadow-xl transition-all border-l-4 group cursor-pointer ${
                item.color === 'green' ? 'border-green-500 hover:border-green-600' :
                item.color === 'orange' ? 'border-orange-500 hover:border-orange-600' :
                item.color === 'blue' ? 'border-blue-500 hover:border-blue-600' : 'border-gray-500'
              }`}
            >
              <div className="text-4xl mb-3">{item.emoji}</div>
              <item.icon className={`w-8 h-8 sm:w-10 sm:h-10 mb-3 sm:mb-4 mx-auto group-hover:scale-110 transition-transform ${
                item.color === 'green' ? 'text-green-600' :
                item.color === 'orange' ? 'text-orange-600' :
                item.color === 'blue' ? 'text-blue-600' : 'text-gray-600'
              }`} />
              <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-sm text-gray-600 mb-2">{item.description}</p>
              <div className={`text-xs font-medium px-3 py-1 rounded-full inline-block ${
                item.color === 'green' ? 'bg-green-100 text-green-700' :
                item.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                item.color === 'blue' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {item.count} options
              </div>
              <div className="mt-3 text-xs text-gray-400 flex items-center justify-center gap-1">
                <span>Click to explore</span>
                <ArrowRight className="w-3 h-3" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    );

    return (
      <div className="space-y-6 sm:space-y-8">
        {activeSection === 'overview' && renderOverview()}
        
        {activeSection === 'methods' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveSection('overview')}
                className="flex items-center gap-2 text-green-600 hover:text-green-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <EnhancedContraceptionCard 
              data={sexualHealthData.contraception.methods}
              title="Contraception Guide"
              subtitle="Explore different protection methods"
              gradientFrom="from-green-500"
              gradientTo="to-emerald-500"
            />
          </div>
        )}
        
        {activeSection === 'emergency' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveSection('overview')}
                className="flex items-center gap-2 text-orange-600 hover:text-orange-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">🚨 Emergency Contraception</h3>
                  <p className="text-gray-600">When you need immediate protection</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {sexualHealthData.contraception.emergencyContraception.map((method, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl p-6 border-l-4 border-orange-500"
                  >
                    <h4 className="text-xl font-bold text-gray-800 mb-4">{method.name}</h4>
                    
                    <div className="space-y-3">
                      <div className="bg-blue-50 rounded-lg p-3">
                        <h5 className="font-semibold text-blue-800 mb-2">How it works:</h5>
                        <p className="text-blue-700 text-sm">
                          {method.name === 'Postinor-2 (Levonorgestrel)' ? 
                            'Prevents or delays ovulation, and may prevent fertilization of an egg by sperm.' :
                            'Contains the same hormone as Postinor-2, works by preventing or delaying ovulation.'
                          }
                        </p>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 font-medium">Time Window:</span>
                        <span className="text-orange-700 font-bold">{method.timeframe}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 font-medium">Effectiveness:</span>
                        <span className="text-green-700 font-bold">{method.effectiveness}</span>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-3">
                        <h5 className="font-semibold text-orange-800 mb-2">📍 Availability:</h5>
                        <p className="text-orange-700 text-sm">{method.availability}</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3">
                        <h5 className="font-semibold text-red-800 mb-2">⚠️ Important:</h5>
                        <p className="text-red-700 text-sm">The sooner you take it, the more effective it is. Consult healthcare provider for personalized advice.</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {activeSection === 'choices' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveSection('overview')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                  <Lightbulb className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Smart Contraception Choices</h3>
                  <p className="text-gray-600">Tips for choosing the right method for you</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { 
                    icon: "🤔", 
                    title: "Consider Your Lifestyle", 
                    desc: "Daily pills vs long-term options",
                    details: [
                      "Are you good with daily routines?",
                      "Do you want reversible or permanent?",
                      "How often do you have sex?",
                      "Do you want to control timing?"
                    ]
                  },
                  { 
                    icon: "💰", 
                    title: "Budget & Insurance", 
                    desc: "Upfront costs vs long-term savings",
                    details: [
                      "Check your insurance coverage",
                      "Compare monthly vs one-time costs",
                      "Consider generic options",
                      "Look into clinic programs"
                    ]
                  },
                  { 
                    icon: "🏥", 
                    title: "Health Factors", 
                    desc: "Medical conditions matter",
                    details: [
                      "Discuss with healthcare provider",
                      "Consider hormonal sensitivities",
                      "Review medication interactions",
                      "Think about side effect tolerance"
                    ]
                  },
                  { 
                    icon: "🎯", 
                    title: "Effectiveness Needs", 
                    desc: "How important is prevention?",
                    details: [
                      "Perfect use vs typical use rates",
                      "Backup method considerations",
                      "STI protection needs",
                      "Pregnancy planning timeline"
                    ]
                  },
                  { 
                    icon: "👥", 
                    title: "Partner Involvement", 
                    desc: "Shared responsibility options",
                    details: [
                      "Male contraception methods",
                      "Barrier methods for both",
                      "Communication about choices",
                      "Backup plan agreements"
                    ]
                  },
                  { 
                    icon: "📅", 
                    title: "Future Plans", 
                    desc: "Think about what's ahead",
                    details: [
                      "When do you want children?",
                      "Career and life timing",
                      "Relationship status considerations",
                      "Reversibility importance"
                    ]
                  }
                ].map((tip, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-lg p-4 border border-blue-200"
                  >
                    <div className="text-center mb-4">
                      <div className="text-3xl mb-2">{tip.icon}</div>
                      <h4 className="font-bold text-gray-800">{tip.title}</h4>
                      <p className="text-gray-600 text-sm">{tip.desc}</p>
                    </div>
                    
                    <div className="space-y-2">
                      {tip.details.map((detail, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-3 h-3 text-blue-600 mt-1 flex-shrink-0" />
                          <span className="text-xs text-gray-700">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        )}
       
      </div>
    );
  };

export const renderContraception = () => <ContraceptionSection />;

export const EnhancedSTICard = ({ data, title, subtitle, gradientFrom, gradientTo, emoji }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    
    const handleNext = () => {
      if (isAnimating) return;
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev + 1) % data.length);
      setTimeout(() => setIsAnimating(false), 300);
    };

    const handlePrev = () => {
      if (isAnimating) return;
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev - 1 + data.length) % data.length);
      setTimeout(() => setIsAnimating(false), 300);
    };

    const currentData = data[currentIndex];

    return (
      <div className={`relative bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-3xl p-1 shadow-2xl hover:shadow-3xl transition-all duration-300`}>
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-3xl blur-sm"></div>
        
        <div className="relative bg-white rounded-3xl p-8 overflow-hidden">
          <div className="absolute top-6 right-6 text-6xl opacity-5">{emoji}</div>
          <div className="absolute bottom-6 left-6 text-8xl opacity-5">{emoji}</div>
          
          <div className="text-center mb-8">
            <div className={`inline-flex items-center gap-3 bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white px-6 py-3 rounded-full mb-4`}>
              <span className="text-2xl">{emoji}</span>
              <span className="font-bold text-lg">{title}</span>
            </div>
            <p className="text-gray-600 text-lg">{subtitle}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="space-y-6">
              <div className={`bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white p-6 rounded-2xl relative overflow-hidden`}>
                <div className="absolute top-0 right-0 text-8xl opacity-10">{emoji}</div>
                <h3 className="text-2xl font-bold mb-2 relative z-10">{currentData.name}</h3>
                <div className="flex gap-2 mb-3">
                  <span className={`text-xs px-3 py-1 rounded-full ${
                    currentData.type === 'Bacterial' ? 'bg-blue-200/30 text-blue-100' :
                    currentData.type === 'Viral' ? 'bg-purple-200/30 text-purple-100' :
                    'bg-green-200/30 text-green-100'
                  }`}>
                    {currentData.type} Infection
                  </span>
                  <span className="text-xs bg-white/20 px-3 py-1 rounded-full">Treatable</span>
                </div>
                <p className="text-white/90 text-sm leading-relaxed">
                  Learn about symptoms, treatment, and prevention methods
                </p>
              </div>

              <div className="bg-yellow-50 rounded-xl p-6 border-l-4 border-yellow-400">
                <h5 className="text-lg font-bold text-yellow-800 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                   Symptoms to Watch For
                </h5>
                <div className="space-y-2">
                  {currentData.symptoms.map((symptom, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <div className="w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Eye className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{symptom}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-green-50 rounded-xl p-6 border-l-4 border-green-400">
                <h5 className="text-lg font-bold text-green-800 mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5" />
                   Treatment Options
                </h5>
                <div className="bg-green-100 rounded-lg p-4">
                  <p className="text-green-800 font-semibold text-lg">{currentData.treatment}</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-xl p-4 border-l-4 border-blue-400">
                  <h5 className="text-sm font-bold text-blue-800 mb-2 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                     Prevention
                  </h5>
                  <p className="text-blue-700 text-sm font-medium">{currentData.prevention}</p>
                </div>
                
                <div className="bg-red-50 rounded-xl p-4 border-l-4 border-red-400">
                  <h5 className="text-sm font-bold text-red-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                     If Untreated
                  </h5>
                  <p className="text-red-700 text-sm font-medium">{currentData.complications}</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-8 border-2 border-red-200">
                <div className="text-center">
                  <div className="text-8xl mb-4">{emoji}</div>
                  <h4 className="text-2xl font-bold text-gray-700 mb-4">{currentData.name}</h4>
                  
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <h5 className="font-bold text-gray-800 mb-2">🏥 Treatment Type</h5>
                      <p className="text-gray-600">{currentData.type === 'Bacterial' ? 'Curable with antibiotics' : 'Manageable with medication'}</p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <h5 className="font-bold text-gray-800 mb-2">🕐 Detection Time</h5>
                      <p className="text-gray-600">
                        {currentData.type === 'Bacterial' ? '1-2 weeks after exposure' : '2-12 weeks for accurate testing'}
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <h5 className="font-bold text-gray-800 mb-2">💰 Treatment Cost</h5>
                      <p className="text-gray-600">Usually affordable and covered by insurance</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <div className="flex space-x-1">
              {data.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    idx === currentIndex 
                      ? `bg-gradient-to-r ${gradientFrom} ${gradientTo}` 
                      : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-2">STI {currentIndex + 1} of {data.length}</div>
              <div className="flex gap-3">
                <button
                  onClick={handlePrev}
                  className={`w-12 h-12 rounded-full bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white flex items-center justify-center hover:scale-110 transition-transform shadow-lg`}
                >
                  <ArrowRight className="w-5 h-5 rotate-180" />
                </button>
                <button
                  onClick={handleNext}
                  className={`w-12 h-12 rounded-full bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white flex items-center justify-center hover:scale-110 transition-transform shadow-lg`}
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="text-right text-xs text-gray-500">
              Type: {currentData.type}<br />
              {currentData.type === 'Bacterial' ? 'Curable ✅' : 'Manageable 💊'}
            </div>
          </div>
        </div>
      </div>
    );
  };

export const STISection = () => {
    const [activeSection, setActiveSection] = useState('overview');
    
    const renderOverview = () => (
      <div className="space-y-6 sm:space-y-8">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-red-500 to-pink-600 rounded-full flex items-center justify-center">
              <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900">STI Information Hub</h2>
              <p className="text-xs sm:text-sm text-gray-600">Knowledge is protection</p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {[
            { 
              id: "stis",
              title: "Common STIs", 
              icon: Stethoscope, 
              color: "red", 
              description: "Learn about symptoms & treatment",
              count: sexualHealthData.sti.commonSTIs.length,
            },
            { 
              id: "testing",
              title: "Testing Schedule", 
              icon: Calendar, 
              color: "blue", 
              description: "When and how often to test",
              count: "Regular",
            },
            { 
              id: "prevention",
              title: "Prevention Tips", 
              icon: Shield, 
              color: "green", 
              description: "Your best defense",
              count: "Essential",
            }
          ].map((item, index) => (
            <motion.button
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setActiveSection(item.id)}
              className={`bg-white rounded-xl shadow-lg p-4 sm:p-6 text-center hover:shadow-xl transition-all border-l-4 group cursor-pointer ${
                item.color === 'red' ? 'border-red-500 hover:border-red-600' :
                item.color === 'blue' ? 'border-blue-500 hover:border-blue-600' :
                item.color === 'green' ? 'border-green-500 hover:border-green-600' : 'border-gray-500'
              }`}
            >
              <div className="text-4xl mb-3">{item.emoji}</div>
              <item.icon className={`w-8 h-8 sm:w-10 sm:h-10 mb-3 sm:mb-4 mx-auto group-hover:scale-110 transition-transform ${
                item.color === 'red' ? 'text-red-600' :
                item.color === 'blue' ? 'text-blue-600' :
                item.color === 'green' ? 'text-green-600' : 'text-gray-600'
              }`} />
              <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-sm text-gray-600 mb-2">{item.description}</p>
              <div className={`text-xs font-medium px-3 py-1 rounded-full inline-block ${
                item.color === 'red' ? 'bg-red-100 text-red-700' :
                item.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                item.color === 'green' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {item.count} info
              </div>
              <div className="mt-3 text-xs text-gray-400 flex items-center justify-center gap-1">
                <span>Click to explore</span>
                <ArrowRight className="w-3 h-3" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    );

    return (
      <div className="space-y-6 sm:space-y-8">
        {activeSection === 'overview' && renderOverview()}
        
        {activeSection === 'stis' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveSection('overview')}
                className="flex items-center gap-2 text-red-600 hover:text-red-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <EnhancedSTICard 
              data={sexualHealthData.sti.commonSTIs}
              title="Common STIs Guide"
              subtitle="Learn about symptoms, treatment & prevention"
              gradientFrom="from-red-500"
              gradientTo="to-pink-500"
            />
          </div>
        )}
        
        {activeSection === 'testing' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveSection('overview')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">STI Testing Schedule</h3>
                  <p className="text-gray-600">When and how often to get tested</p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-blue-100 rounded-lg p-6">
                  <h4 className="text-xl font-bold text-blue-800 mb-4">Frequency Guidelines</h4>
                  <p className="text-blue-700 font-medium text-lg">{sexualHealthData.sti.testing.frequency}</p>
                </div>
                
                <div className="bg-white rounded-lg p-6 border border-blue-200">
                  <h4 className="text-xl font-bold text-gray-800 mb-4">🔬 Available Test Types</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {sexualHealthData.sti.testing.types.map((type, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg"
                      >
                        <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <span className="text-gray-700 font-medium">{type}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeSection === 'prevention' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveSection('overview')}
                className="flex items-center gap-2 text-green-600 hover:text-green-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">STI Prevention Guide</h3>
                  <p className="text-gray-600">Your best defense is knowledge</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  { icon: "🫧", title: "Use Protection", desc: "Condoms and dental dams for all sexual activity", details: ["Use latex or polyurethane condoms", "Don't reuse or share", "Check expiration dates", "Store properly"] },
                  { icon: "🤝", title: "Communicate", desc: "Discuss sexual history with partners openly", details: ["Ask about recent testing", "Share your status", "Discuss boundaries", "Plan together"] },
                  { icon: "🩺", title: "Regular Testing", desc: "Get tested together before unprotected sex", details: ["Test before new partners", "Annual screenings minimum", "Know your status", "Follow up on results"] },
                  { icon: "💉", title: "Vaccinations", desc: "HPV and Hepatitis B vaccines available", details: ["HPV vaccine up to age 26", "Hepatitis B series", "Ask your doctor", "Stay up to date"] }
                ].map((tip, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-lg p-4 border border-green-200"
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className="text-2xl">{tip.icon}</div>
                      <div>
                        <h4 className="font-bold text-gray-800 mb-1">{tip.title}</h4>
                        <p className="text-gray-600 text-sm">{tip.desc}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {tip.details.map((detail, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-3 h-3 text-green-600 mt-1 flex-shrink-0" />
                          <span className="text-xs text-gray-700">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        )}

        
      </div>
    );
  };

export  const renderSTI = () => <STISection />;

export const EnhancedPleasureCard = ({ data, title, subtitle, gradientFrom, gradientTo, cardId, emoji }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    
    const handleNext = () => {
      if (isAnimating) return;
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev + 1) % data.length);
      setTimeout(() => setIsAnimating(false), 300);
    };

    const handlePrev = () => {
      if (isAnimating) return;
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev - 1 + data.length) % data.length);
      setTimeout(() => setIsAnimating(false), 300);
    };

    const currentData = data[currentIndex];

    return (
      <div className={`relative bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-2xl p-1 shadow-2xl hover:shadow-3xl transition-all duration-300`}>
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-2xl blur-sm"></div>
        
        <div className="relative bg-white rounded-2xl p-6 h-[650px] overflow-hidden">
          <div className="absolute top-4 right-4 text-4xl opacity-10">{emoji}</div>
          <div className="absolute bottom-4 left-4 text-6xl opacity-5">{emoji}</div>
          
          <div className="mb-6">
            <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white px-4 py-2 rounded-full mb-3`}>
              <span className="text-lg">{emoji}</span>
              <span className="font-semibold text-sm">{title}</span>
            </div>
            <p className="text-gray-600 text-sm">{subtitle}</p>
          </div>

          <div className="flex justify-between items-center mb-6">
            <div className="flex space-x-1">
              {data.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    idx === currentIndex 
                      ? `bg-gradient-to-r ${gradientFrom} ${gradientTo}` 
                      : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handlePrev}
                className={`w-8 h-8 rounded-full bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white flex items-center justify-center hover:scale-110 transition-transform shadow-lg`}
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
              </button>
              <button
                onClick={handleNext}
                className={`w-8 h-8 rounded-full bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white flex items-center justify-center hover:scale-110 transition-transform shadow-lg`}
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <div className={`bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white p-4 rounded-xl relative overflow-hidden`}>
              <div className="absolute top-0 right-0 text-6xl opacity-20">{emoji}</div>
              <h3 className="text-xl font-bold mb-1 relative z-10">{currentData.name}</h3>
              <div className="text-xs bg-white/20 inline-block px-2 py-1 rounded-full">
                Tip {currentIndex + 1} of {data.length}
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 border-l-4 border-gradient-to-b from-pink-400 to-purple-400">
              <p className="text-gray-700 text-sm leading-relaxed font-medium">{currentData.description}</p>
            </div>

            <div className="bg-pink-50 rounded-xl p-4 border-l-4 border-pink-400">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Star className="w-4 h-4 text-pink-600" />
                Expert Techniques
              </h4>
              <div className="space-y-2">
                {currentData.techniques.slice(0, 4).map((technique, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <div className={`w-5 h-5 rounded-full bg-gradient-to-r ${gradientFrom} ${gradientTo} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm text-gray-700 font-medium">{technique}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation hint */}
            <div className="text-center pt-4">
              <div className="inline-flex items-center gap-2 text-xs text-gray-500 bg-gray-100 px-3 py-2 rounded-full">
                <ArrowRight className="w-3 h-3" />
                <span>Swipe for more {emoji} techniques</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  };

export const EnhancedPositionsCard = ({ data, title, subtitle, gradientFrom, gradientTo, cardId, emoji }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    
    const handleNext = () => {
      if (isAnimating) return;
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev + 1) % data.length);
      setTimeout(() => setIsAnimating(false), 300);
    };

    const handlePrev = () => {
      if (isAnimating) return;
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev - 1 + data.length) % data.length);
      setTimeout(() => setIsAnimating(false), 300);
    };

    const currentData = data[currentIndex];

    return (
      <div className={`relative bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-3xl p-1 shadow-2xl hover:shadow-3xl transition-all duration-300`}>
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-3xl blur-sm"></div>
        
        <div className="relative bg-white rounded-3xl p-8 overflow-hidden">
          <div className="absolute top-6 right-6 text-6xl opacity-5">{emoji}</div>
          <div className="absolute bottom-6 left-6 text-8xl opacity-5">{emoji}</div>
          
          <div className="text-center mb-8">
            <div className={`inline-flex items-center gap-3 bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white px-6 py-3 rounded-full mb-4`}>
              <span className="text-2xl">{emoji}</span>
              <span className="font-bold text-lg">{title}</span>
            </div>
            <p className="text-gray-600 text-lg">{subtitle}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <div className={`bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white p-6 rounded-2xl relative overflow-hidden`}>
                <div className="absolute top-0 right-0 text-8xl opacity-10">{emoji}</div>
                <h3 className="text-2xl font-bold mb-2 relative z-10">{currentData.name}</h3>
                <div className="flex gap-2 mb-3">
                  <span className="text-xs bg-white/20 px-3 py-1 rounded-full">{currentData.difficulty}</span>
                  <span className="text-xs bg-white/20 px-3 py-1 rounded-full">{currentData.pleasureLevel}</span>
                </div>
                <p className="text-white/90 text-sm leading-relaxed">{currentData.description}</p>
              </div>

              {currentData.entryTechnique && (
                <div className="bg-blue-50 rounded-xl p-4 border-l-4 border-blue-400">
                  <h5 className="text-sm font-bold text-blue-800 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                     How To Enter
                  </h5>
                  <p className="text-blue-700 text-sm font-medium">{currentData.entryTechnique}</p>
                </div>
              )}

              {currentData.bestFor && (
                <div className="bg-green-50 rounded-xl p-4 border-l-4 border-green-400">
                  <h5 className="text-sm font-bold text-green-800 mb-2 flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    Best For
                  </h5>
                  <p className="text-green-700 text-sm font-medium">{currentData.bestFor}</p>
                </div>
              )}

              {currentData.benefits && (
                <div className="bg-purple-50 rounded-xl p-4 border-l-4 border-purple-400">
                  <h5 className="text-sm font-bold text-purple-800 mb-2 flex items-center gap-2">
                    <Heart className="w-4 h-4" />
                    Benefits
                  </h5>
                  <ul className="space-y-1">
                    {currentData.benefits.slice(0, 3).map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-3 h-3 text-purple-600 mt-1 flex-shrink-0" />
                        <span className="text-purple-700 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl p-8 flex items-center justify-center border-2 border-pink-200">
                <div className="text-center">
                  <div className="text-8xl mb-4">{emoji}</div>
                  <h4 className="text-2xl font-bold text-gray-700 mb-2">{currentData.name}</h4>
                  <p className="text-gray-600">Visual guide coming soon</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <div className="flex space-x-1">
              {data.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    idx === currentIndex 
                      ? `bg-gradient-to-r ${gradientFrom} ${gradientTo}` 
                      : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-2">Position {currentIndex + 1} of {data.length}</div>
              <div className="flex gap-3">
                <button
                  onClick={handlePrev}
                  className={`w-12 h-12 rounded-full bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white flex items-center justify-center hover:scale-110 transition-transform shadow-lg`}
                >
                  <ArrowRight className="w-5 h-5 rotate-180" />
                </button>
                <button
                  onClick={handleNext}
                  className={`w-12 h-12 rounded-full bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white flex items-center justify-center hover:scale-110 transition-transform shadow-lg`}
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="text-right text-xs text-gray-500">
              Difficulty: {currentData.difficulty}<br />
              Pleasure: {currentData.pleasureLevel}
            </div>
          </div>
        </div>
      </div>
    );
  };

export const PleasureSection = () => {
    const [activeSection, setActiveSection] = useState('overview');
    
    const renderOverview = () => (
      <div className="space-y-6 sm:space-y-8">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
              <Star className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Sexual Wellness & Pleasure</h2>
              <p className="text-xs sm:text-sm text-gray-600">Your guide to intimacy and satisfaction</p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {[
            { 
              id: "orgasm",
              title: "Orgasm Guide", 
              icon: Star, 
              color: "pink", 
              description: "Master the art of climax",
              count: sexualHealthData.sexualPleasure.orgasmGuide.length,
            },
            { 
              id: "techniques",
              title: "Pleasure Techniques", 
              icon: Heart, 
              color: "red", 
              description: "Advanced methods & tips",
              count: sexualHealthData.sexualPleasure.pleasureTechniques.length,
            },
            { 
              id: "positions",
              title: "Positions Guide", 
              icon: Lightbulb, 
              color: "purple", 
              description: "Visual guide to amazing positions",
              count: "Essential",
            }
          ].map((item, index) => (
            <motion.button
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setActiveSection(item.id)}
              className={`bg-white rounded-xl shadow-lg p-4 sm:p-6 text-center hover:shadow-xl transition-all border-l-4 group cursor-pointer ${
                item.color === 'pink' ? 'border-pink-500 hover:border-pink-600' :
                item.color === 'red' ? 'border-red-500 hover:border-red-600' :
                item.color === 'purple' ? 'border-purple-500 hover:border-purple-600' : 'border-gray-500'
              }`}
            >
              <div className="text-4xl mb-3">{item.emoji}</div>
              <item.icon className={`w-8 h-8 sm:w-10 sm:h-10 mb-3 sm:mb-4 mx-auto group-hover:scale-110 transition-transform ${
                item.color === 'pink' ? 'text-pink-600' :
                item.color === 'red' ? 'text-red-600' :
                item.color === 'purple' ? 'text-purple-600' : 'text-gray-600'
              }`} />
              <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-sm text-gray-600 mb-2">{item.description}</p>
              <div className={`text-xs font-medium px-3 py-1 rounded-full inline-block ${
                item.color === 'pink' ? 'bg-pink-100 text-pink-700' :
                item.color === 'red' ? 'bg-red-100 text-red-700' :
                item.color === 'purple' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {item.count} tips
              </div>
              <div className="mt-3 text-xs text-gray-400 flex items-center justify-center gap-1">
                <span>Click to explore</span>
                <ArrowRight className="w-3 h-3" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    );

    return (
      <div className="space-y-6 sm:space-y-8">
        {activeSection === 'overview' && renderOverview()}
        
        {activeSection === 'orgasm' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveSection('overview')}
                className="flex items-center gap-2 text-pink-600 hover:text-pink-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <EnhancedPleasureCard 
              data={sexualHealthData.sexualPleasure.orgasmGuide}
              title="Ultimate Orgasm Guide"
              subtitle="Master the art of climax"
              gradientFrom="from-pink-500"
              gradientTo="to-rose-500"
              cardId="orgasm-guide"
            />
          </div>
        )}
        
        {activeSection === 'techniques' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveSection('overview')}
                className="flex items-center gap-2 text-red-600 hover:text-red-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <EnhancedPleasureCard 
              data={sexualHealthData.sexualPleasure.pleasureTechniques}
              title="🔥 Pleasure Mastery"
              subtitle="Advanced techniques that drive wild"
              gradientFrom="from-red-500"
              gradientTo="to-orange-500"
              cardId="pleasure-techniques"
            />
          </div>
        )}
        
        {activeSection === 'positions' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveSection('overview')}
                className="flex items-center gap-2 text-purple-600 hover:text-purple-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <EnhancedPositionsCard 
              data={[...sexualHealthData.sexualIntimacy.positions.beginner, ...sexualHealthData.sexualIntimacy.positions.intermediate]}
              title="🔥 Sex Positions Guide"
              subtitle="Visual guide to amazing positions"
              gradientFrom="from-purple-500"
              gradientTo="to-indigo-500"
              cardId="positions-guide"
            />
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl p-4 sm:p-6 border border-pink-200"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-pink-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Pro Tips for Amazing Experiences</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="w-5 h-5 bg-pink-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-gray-700 text-sm">Communication is the ultimate aphrodisiac</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-gray-700 text-sm">Take time with foreplay - minimum 15 minutes</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-gray-700 text-sm">Focus on pleasure, not just performance</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-gray-700 text-sm">Every body is different - explore what works</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

 export const renderPleasure = () => <PleasureSection />;

export const SafetySection = () => {
    const [activeSection, setActiveSection] = useState('overview');
    
    const randomPhysicalSafety = sexualHealthData.sexualSafety.getRandomSafety('PhysicalSafety', 3);
    const randomEmotionalSafety = sexualHealthData.sexualSafety.getRandomSafety('EmotionalSafety', 2);
    const randomRiskReduction = sexualHealthData.sexualSafety.getRandomSafety('RiskReduction', 2);

    const renderOverview = () => (
      <div className="space-y-6 sm:space-y-8">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Sexual Safety Hub</h2>
              <p className="text-xs sm:text-sm text-gray-600">Physical, emotional, and digital safety</p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {[
            { 
              id: "physical",
              title: "Physical Safety", 
              icon: Shield, 
              color: "orange", 
              description: "Evidence-based safety tips",
              count: sexualHealthData.sexualSafety.allPhysicalSafety.length,
            },
            { 
              id: "emotional", 
              title: "Emotional Safety", 
              icon: Heart, 
              color: "pink", 
              description: "Boundaries & communication",
              count: sexualHealthData.sexualSafety.allEmotionalSafety.length,
            },
            { 
              id: "risks",
              title: "Risk Reduction", 
              icon: Target, 
              color: "blue", 
              description: "Smart prevention strategies",
              count: sexualHealthData.sexualSafety.allRiskReduction.length,
            }
          ].map((item, index) => (
            <motion.button
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setActiveSection(item.id)}
              className={`bg-white rounded-xl shadow-lg p-4 sm:p-6 text-center hover:shadow-xl transition-all border-l-4 group cursor-pointer ${
                item.color === 'orange' ? 'border-orange-500 hover:border-orange-600' :
                item.color === 'pink' ? 'border-pink-500 hover:border-pink-600' :
                item.color === 'blue' ? 'border-blue-500 hover:border-blue-600' : 'border-gray-500'
              }`}
            >
              <div className="text-4xl mb-3">{item.emoji}</div>
              <item.icon className={`w-8 h-8 sm:w-10 sm:h-10 mb-3 sm:mb-4 mx-auto group-hover:scale-110 transition-transform ${
                item.color === 'orange' ? 'text-orange-600' :
                item.color === 'pink' ? 'text-pink-600' :
                item.color === 'blue' ? 'text-blue-600' : 'text-gray-600'
              }`} />
              <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-sm text-gray-600 mb-2">{item.description}</p>
              <div className={`text-xs font-medium px-3 py-1 rounded-full inline-block ${
                item.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                item.color === 'pink' ? 'bg-pink-100 text-pink-700' :
                item.color === 'blue' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {item.count} tips
              </div>
              <div className="mt-3 text-xs text-gray-400 flex items-center justify-center gap-1">
                <span>Click to explore</span>
                <ArrowRight className="w-3 h-3" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    );

    return (
      <div className="space-y-6 sm:space-y-8">
        {activeSection === 'overview' && renderOverview()}
        
        {activeSection === 'physical' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveSection('overview')}
                className="flex items-center gap-2 text-orange-600 hover:text-orange-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Physical Safety Essentials</h3>
                  <p className="text-gray-600">Evidence-based safety guidelines</p>
                </div>
              </div>
              
              <div className="grid lg:grid-cols-3 gap-4">
                {randomPhysicalSafety.map((safety, index) => (
                  <motion.div
                    key={safety.topic}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl p-4 border-l-4 border-orange-500"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="text-xl">{safety.icon}</div>
                      <h4 className="font-bold text-gray-800">{safety.topic}</h4>
                      {safety.priority === 'high' && (
                        <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full font-medium">
                          High Priority
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      {safety.details.slice(0, 3).map((detail, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-3 h-3 text-green-600 mt-1 flex-shrink-0" />
                          <span className="text-xs text-gray-700 leading-relaxed">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {activeSection === 'emotional' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveSection('overview')}
                className="flex items-center gap-2 text-pink-600 hover:text-pink-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Emotional Safety</h3>
                  <p className="text-gray-600">Boundaries & communication</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {randomEmotionalSafety.map((safety, index) => (
                  <motion.div
                    key={safety.topic}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-lg p-4 border border-pink-200"
                  >
                    <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">💭</span>
                      {safety.topic}
                    </h4>
                    
                    <div className="space-y-2">
                      {safety.details.map((detail, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Heart className="w-3 h-3 text-pink-600 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {activeSection === 'risks' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveSection('overview')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Risk Reduction</h3>
                  <p className="text-gray-600">Smart prevention strategies</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {randomRiskReduction.map((risk, index) => (
                  <motion.div
                    key={risk.category}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-lg p-4 border border-blue-200"
                  >
                    <h4 className="font-bold text-gray-800 mb-2">{risk.category}</h4>
                    
                    <div className="space-y-2">
                      {risk.strategies.map((strategy, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Target className="w-3 h-3 text-blue-600 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{strategy}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        )}

       
      </div>
    );
  };

 export const renderSafety = () => <SafetySection />;